import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet, faArrowUp, faArrowDown, faChartLine,
  faMoneyBillWave, faExchangeAlt, faSyncAlt,
  faCheckCircle, faExclamationTriangle, faSpinner,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// ============================================================
// UTILITAIRES ET CACHE
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
  }
};

const db = new Dexie('DashboardCaisseCacheDB');
db.version(1).stores({
  stats: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

const getCachedStats = async (key, allowExpired = false) => {
  try {
    const entry = await db.stats.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
};

const setCachedStats = async (key, data) => {
  await db.stats.put({ key, data, timestamp: Date.now() });
};

const getStaleStats = (key) => getCachedStats(key, true);

const API_URL = `${API_CONFIG2.apiUrl}caisse/api_dashboard_caisse.php`;

// ============================================================
// HOOK PERSONNALISÉ : useDashboardData (avec dédoublonnage)
// ============================================================
const pendingFetches = new Map();

const useDashboardData = (userId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');
  const abortControllerRef = useRef(null);
  const cacheKey = 'dashboard_caisse';

  const fetchDashboard = useCallback(async (forceRefresh = false, retryCount = 0) => {
    // Annulation de la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');
    setError(null);

    // 1. Dédoublonnage : vérifier si une requête est déjà en cours pour cette clé
    const pendingKey = `${cacheKey}_${forceRefresh}`;
    if (pendingFetches.has(pendingKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(pendingKey);
        if (result) {
          setData(result);
          setDataSource('cache (partagé)');
        }
        setLoading(false);
        setRefreshing(false);
        return result;
      } catch (err) {
        pendingFetches.delete(pendingKey);
        // on continue pour relancer une requête
      }
    }

    // 2. Lecture du cache (sauf si forceRefresh)
    if (!forceRefresh) {
      const cached = await getCachedStats(cacheKey);
      if (cached) {
        setData(cached);
        setDataSource('cache');
        setLoading(false);
        setRefreshing(false);
        return cached;
      }
    }

    // 3. Création de la promesse de requête
    const fetchPromise = (async () => {
      try {
        const url = API_URL + (forceRefresh ? '?_t=' + Date.now() : '');
        const response = await fetch(url, { signal: controller.signal });

        if (response.status === 429) {
          const stale = await getStaleStats(cacheKey);
          if (stale) {
            setData(stale);
            setDataSource('cache (périmé)');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchDashboard(forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleStats(cacheKey);
          if (stale) {
            setData(stale);
            setDataSource('cache (périmé)');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const json = await response.json();
        if (json.status === 'success') {
          setData(json.data);
          setDataSource(forceRefresh ? 'live (forcé)' : 'live');
          await setCachedStats(cacheKey, json.data);
          setLoading(false);
          setRefreshing(false);
          return json.data;
        } else {
          throw new Error(json.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        console.error('fetchDashboard error:', err);
        const stale = await getStaleStats(cacheKey);
        if (stale) {
          setData(stale);
          setDataSource('cache (périmé)');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        setError(err.message);
        setLoading(false);
        setRefreshing(false);
        throw err;
      }
    })();

    pendingFetches.set(pendingKey, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(pendingKey);
    }
  }, []);

  const refresh = useCallback(() => {
    if (!refreshing && !loading) {
      fetchDashboard(true);
    }
  }, [fetchDashboard, refreshing, loading]);

  const clearAndRefresh = useCallback(() => {
    if (!refreshing && !loading) {
      db.stats.clear();
      fetchDashboard(true);
    }
  }, [fetchDashboard, refreshing, loading]);

  useEffect(() => {
    fetchDashboard();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchDashboard]);

  return { data, loading, refreshing, error, dataSource, refresh, clearAndRefresh };
};

// ============================================================
// SOUS-COMPOSANTS (avec classes CSS pour la responsivité)
// ============================================================
const KpiCard = ({ icon, label, value, sub, color = 'blue' }) => (
  <div className="kpi-card" role="listitem">
    <div className="kpi-icon" style={{ color: `var(--accent-${color})` }}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="kpi-content">
      <div className="kpi-value" style={{ color: `var(--accent-${color})` }}>
        {value}
      </div>
      <div className="kpi-label">{label}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  </div>
);

const KpiSkeleton = () => (
  <div className="kpi-card skeleton" style={{ opacity: 0.6, pointerEvents: 'none' }}>
    <div className="kpi-icon" style={{ background: 'var(--card-bg)', borderRadius: '50%', width: 50, height: 50 }} />
    <div className="kpi-content">
      <div className="kpi-value" style={{ height: '1.8rem', width: '60%', background: 'var(--card-border)', borderRadius: 4 }} />
      <div className="kpi-label" style={{ height: '0.9rem', width: '40%', background: 'var(--card-border)', borderRadius: 4, marginTop: 8 }} />
    </div>
  </div>
);

const RecentMovements = ({ movements }) => (
  <div className="glass-card" style={{ maxHeight: '400px', display: 'flex', flexDirection: 'column' }}>
    <h5 className="fw-bold mb-3" style={{ color: 'var(--accent-blue)', fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
      <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
      Derniers mouvements
    </h5>
    <div className="table-responsive" style={{ flex: 1, overflowY: 'auto' }}>
      <table className="table-caisse" aria-label="Liste des derniers mouvements de caisse">
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Montant</th>
            <th scope="col">Description</th>
            <th scope="col">Solde</th>
            <th scope="col">Date</th>
          </tr>
        </thead>
        <tbody>
          {movements.map(m => (
            <tr key={m.id}>
              <td><span className={`badge-type ${m.type}`}>{m.type}</span></td>
              <td className={m.type === 'Entrée' ? 'text-success' : 'text-danger'}>
                {m.type === 'Entrée' ? '+' : '-'}{m.montant.toFixed(2)} USD
              </td>
              <td className="desc-cell">{m.description || '-'}</td>
              <td>{m.solde_apres.toFixed(2)} USD</td>
              <td className="date-cell text-secondary small">{new Date(m.date).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ChartsSection = ({ evolution, repartition, topDescriptions }) => {
  const evolutionData = useMemo(() => ({
    labels: evolution.map(e => e.mois),
    datasets: [
      {
        label: 'Entrées',
        data: evolution.map(e => e.entrees),
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: '#3b82f6',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 3,
      },
      {
        label: 'Sorties',
        data: evolution.map(e => e.sorties),
        backgroundColor: 'rgba(239, 68, 68, 0.2)',
        borderColor: '#ef4444',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 3,
      },
    ],
  }), [evolution]);

  const repartitionData = useMemo(() => ({
    labels: repartition.map(r => r.type),
    datasets: [{
      data: repartition.map(r => r.total_montant),
      backgroundColor: ['#3b82f6', '#ef4444'],
      borderColor: '#0b1428',
      borderWidth: 2,
    }],
  }), [repartition]);

  const topDescData = useMemo(() => ({
    labels: topDescriptions.map(d => d.description),
    datasets: [{
      label: 'Montant total',
      data: topDescriptions.map(d => d.total_montant),
      backgroundColor: 'rgba(59, 130, 246, 0.6)',
      borderColor: '#3b82f6',
      borderWidth: 1,
      borderRadius: 4,
    }],
  }), [topDescriptions]);

  return (
    <>
      <div className="glass-card">
        <div className="charts-grid">
          <div className="chart-box" role="figure" aria-label="Graphique de l'évolution mensuelle">
            <div className="chart-title">
              <FontAwesomeIcon icon={faChartLine} /> Évolution mensuelle
            </div>
            <Line data={evolutionData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
          <div className="chart-box" role="figure" aria-label="Répartition des montants">
            <div className="chart-title">
              <FontAwesomeIcon icon={faMoneyBillWave} /> Répartition
            </div>
            <Doughnut data={repartitionData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      <div className="glass-card">
        <h5 className="fw-bold mb-3" style={{ color: 'var(--accent-blue)', fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
          <FontAwesomeIcon icon={faChartLine} className="me-2" />
          Top 5 descriptions
        </h5>
        <div style={{ height: '200px', minHeight: '150px' }}>
          <Bar
            data={topDescData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: 'y',
              plugins: { legend: { display: false } },
            }}
          />
        </div>
      </div>
    </>
  );
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const DashboardCaisse = ({ userId }) => {
  const { data, loading, refreshing, error, dataSource, refresh, clearAndRefresh } = useDashboardData(userId);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const toastTimeoutRef = useRef(null);

  const showToast = useCallback((message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  useEffect(() => {
    if (error) {
      showToast(error, 'danger');
    }
  }, [error, showToast]);

  const fmt = (n) => n?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' USD' || '0 USD';

  if (loading && !data && !refreshing) {
    return (
      <div className="dashboard-caisse">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
          <h2 className="dashboard-title"><FontAwesomeIcon icon={faWallet} className="me-2" style={{ color: 'var(--accent-blue)' }} /> Dashboard Caisse</h2>
        </div>
        <div className="kpi-grid">
          {[...Array(4)].map((_, i) => <KpiSkeleton key={i} />)}
        </div>
        <div className="glass-card" style={{ height: '250px' }} />
        <div className="glass-card" style={{ height: '200px' }} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="dashboard-caisse text-center py-5">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-danger mb-2" />
        <p className="text-secondary">{error}</p>
        <button className="monetra-btn primary" onClick={refresh}>
          <FontAwesomeIcon icon={faSyncAlt} /> Réessayer
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-red: #ef4444;
          --accent-green: #10b981;
          --bg-primary: #0b1428;
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0,0,0,0.5);
        }

        .dashboard-caisse {
          padding: 30px;
          background: var(--bg-primary);
          background-image: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          min-height: 100vh;
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          margin-bottom: 24px;
          transition: border-color 0.2s;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
        }

        .dashboard-title {
          font-size: clamp(1.2rem, 4vw, 2rem);
          font-weight: 700;
          margin-bottom: 0;
        }

        .badge-source {
          font-size: clamp(0.65rem, 1.2vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1.2vw, 0.9rem);
          white-space: nowrap;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.25);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: #1d4ed8;
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }
        .kpi-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          transition: transform 0.2s, border-color 0.2s;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          border-color: rgba(59, 130, 246, 0.25);
        }
        .kpi-icon {
          font-size: clamp(1.5rem, 3vw, 2.2rem);
          width: clamp(40px, 8vw, 55px);
          height: clamp(40px, 8vw, 55px);
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.04);
          border-radius: 50%;
          flex-shrink: 0;
        }
        .kpi-value {
          font-size: clamp(1.2rem, 3.5vw, 1.9rem);
          font-weight: 700;
          line-height: 1.2;
        }
        .kpi-label {
          font-size: clamp(0.7rem, 1.2vw, 0.85rem);
          color: var(--text-secondary);
          font-weight: 500;
        }
        .kpi-sub {
          font-size: clamp(0.6rem, 1vw, 0.75rem);
          color: var(--text-muted);
        }

        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .chart-box {
          height: 220px;
          min-height: 150px;
          position: relative;
        }
        .chart-title {
          font-size: clamp(0.7rem, 1.2vw, 0.9rem);
          color: var(--text-secondary);
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-danger { color: #ef4444; }
        .text-success { color: #10b981; }
        .text-center { text-align: center; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .mt-2 { margin-top: 0.5rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .fw-bold { font-weight: 700; }

        .table-caisse {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.7rem, 1vw, 0.85rem);
        }
        .table-caisse th {
          padding: 10px 6px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .table-caisse td {
          padding: 10px 6px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          color: var(--text-secondary);
        }
        .table-caisse tr:hover td {
          background: rgba(255,255,255,0.03);
        }
        .desc-cell {
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .date-cell {
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
        }

        .badge-type {
          padding: 3px 10px;
          border-radius: 20px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          font-weight: 600;
          display: inline-block;
        }
        .badge-type.Entrée {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }
        .badge-type.Sortie {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .toast-custom {
          background: rgba(10, 20, 45, 0.85);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          color: var(--text-primary);
          box-shadow: 0 10px 30px rgba(0,0,0,0.6);
          animation: slideIn 0.3s ease-out;
          font-size: clamp(0.8rem, 1.2vw, 0.95rem);
        }
        @keyframes slideIn {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .toast-custom.danger { border-color: #ef4444; }
        .toast-custom.success { border-color: #10b981; }
        .toast-custom.warning { border-color: #f59e0b; }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-primary);
          opacity: 0.5;
          font-size: 1.2rem;
          cursor: pointer;
          padding: 0 4px;
        }
        .btn-close-custom:hover { opacity: 1; }

        /* RESPONSIVITÉ MOBILE */
        @media (max-width: 768px) {
          .dashboard-caisse { padding: 16px; }
          .kpi-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .kpi-card {
            padding: 0.8rem;
            gap: 0.5rem;
          }
          .charts-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          .chart-box {
            height: 180px;
          }
          .glass-card {
            padding: 1rem;
          }
          .table-caisse th, .table-caisse td {
            padding: 6px 4px;
          }
          .desc-cell {
            max-width: 80px;
          }
          .monetra-btn {
            padding: 6px 12px;
            font-size: 0.7rem;
          }
          .badge-source {
            font-size: 0.6rem;
            padding: 2px 8px;
          }
          .dashboard-title {
            font-size: 1rem;
          }
          .toast-custom {
            font-size: 0.8rem;
          }
        }

        @media (max-width: 480px) {
          .kpi-grid {
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .kpi-card {
            flex-direction: column;
            align-items: flex-start;
            padding: 0.6rem;
          }
          .kpi-icon {
            width: 32px;
            height: 32px;
            font-size: 1.2rem;
          }
          .kpi-value {
            font-size: 1rem;
          }
          .kpi-label {
            font-size: 0.6rem;
          }
          .kpi-sub {
            font-size: 0.5rem;
          }
          .chart-box {
            height: 140px;
          }
          .glass-card {
            padding: 0.8rem;
            margin-bottom: 16px;
          }
          .desc-cell {
            max-width: 60px;
          }
          .date-cell {
            font-size: 0.55rem;
          }
          .badge-type {
            font-size: 0.55rem;
            padding: 2px 6px;
          }
          .table-caisse {
            font-size: 0.6rem;
          }
          .monetra-btn {
            font-size: 0.65rem;
            padding: 4px 10px;
          }
          .badge-source {
            font-size: 0.55rem;
            padding: 2px 6px;
          }
          .dashboard-title {
            font-size: 0.9rem;
          }
        }

        /* Pour les très petits écrans, on réduit le texte */
        @media (max-width: 360px) {
          .kpi-grid {
            grid-template-columns: 1fr 1fr;
            gap: 6px;
          }
          .kpi-card {
            padding: 0.4rem;
          }
          .kpi-value {
            font-size: 0.8rem;
          }
          .kpi-label {
            font-size: 0.5rem;
          }
          .table-caisse th, .table-caisse td {
            padding: 4px 2px;
          }
          .desc-cell {
            max-width: 40px;
          }
          .date-cell {
            font-size: 0.5rem;
          }
        }
      `}</style>

      <div className="dashboard-caisse">
        {/* En-tête */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <h2 className="dashboard-title">
            <FontAwesomeIcon icon={faWallet} className="me-2" style={{ color: 'var(--accent-blue)' }} />
            Dashboard Caisse
          </h2>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn outline"
              onClick={refresh}
              disabled={refreshing || loading}
              aria-label="Actualiser les données"
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn outline"
              onClick={clearAndRefresh}
              disabled={refreshing || loading}
              aria-label="Vider le cache et recharger"
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="kpi-grid" role="list">
          <KpiCard
            icon={faMoneyBillWave}
            label="Solde actuel"
            value={fmt(data.solde_actuel)}
            color="blue"
          />
          <KpiCard
            icon={faArrowUp}
            label="Total entrées"
            value={fmt(data.total_entrees)}
            sub={`Moyenne: ${fmt(data.moyennes.moyenne_entree)}`}
            color="green"
          />
          <KpiCard
            icon={faArrowDown}
            label="Total sorties"
            value={fmt(data.total_sorties)}
            sub={`Moyenne: ${fmt(data.moyennes.moyenne_sortie)}`}
            color="red"
          />
          <KpiCard
            icon={faExchangeAlt}
            label="Différence"
            value={fmt(data.difference)}
            sub={`${data.nb_mouvements} mouvements`}
            color={data.difference >= 0 ? 'green' : 'red'}
          />
        </div>

        {/* Graphiques */}
        <ChartsSection
          evolution={data.evolution}
          repartition={data.repartition}
          topDescriptions={data.top_descriptions}
        />

        {/* Derniers mouvements */}
        <RecentMovements movements={data.derniers_mouvements} />

        {/* Toast */}
        {toast.show && (
          <div
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 9999,
              maxWidth: '400px',
              width: '90%',
            }}
            role="alert"
          >
            <div className={`toast-custom p-3 ${toast.variant}`}>
              <div className="d-flex align-items-center gap-3">
                <FontAwesomeIcon
                  icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                  className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
                />
                <span style={{ flex: 1 }}>{toast.message}</span>
                <button
                  className="btn-close-custom"
                  onClick={() => setToast({ ...toast, show: false })}
                  aria-label="Fermer le message"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardCaisse;